if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Update to use PUB_SUB_EVENTS
        window.addEventListener('DOMContentLoaded', () => {
          subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const { variant } = event.data;
            this.handleVariantChange(variant);
          });

          const firstImage = this.elements.viewer?.querySelector('[data-media-id] img');
          if (firstImage && firstImage.alt) {
            this.handleVariantChange({ featured_media: { alt: firstImage.alt } });
          }
        });

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      handleVariantChange(variant) {
        if (!variant) return;

        if (variant.featured_media && variant.featured_media.alt) {
          const currentImgAlt = variant.featured_media.alt.toLowerCase();

          // Extract the dynamic part from currentImgAlt
          const altParts = currentImgAlt.split('_');
          const matchingAlt = altParts.length >= 3 ? `eam_all_${altParts.slice(2).join('_')}` : null;

          // Get ignoreSharedImages value from selected swatch
          const checkedInput = document.querySelector('.swatch-input__input[type="radio"]:checked');
          const swatchLabel = checkedInput ? checkedInput.nextElementSibling : null;
          const ignoreSharedImages = swatchLabel?.getAttribute('data-ignore-shared-images') === 'true';

          // Hide all thumbnails first
          this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((thumbnail) => {
            const img = thumbnail.querySelector('img');
            if (img) {
              const imgAlt = img.alt.toLowerCase();
              // New: check for _all matching
              let allMatch = false;
              if (imgAlt.endsWith('_all')) {
                const allPrefix = imgAlt.slice(0, -4); // remove '_all'
                allMatch = currentImgAlt.startsWith(allPrefix);
              }
              if (ignoreSharedImages) {
                // Hide only 'eam_all', but allow _all prefix matches
                thumbnail.style.display =
                  imgAlt === 'eam_all'
                    ? 'none'
                    : imgAlt === currentImgAlt || imgAlt === matchingAlt || allMatch
                    ? 'block'
                    : 'none';
              } else {
                thumbnail.style.display =
                  imgAlt === 'eam_all' || imgAlt === currentImgAlt || imgAlt === matchingAlt || allMatch
                    ? 'block'
                    : 'none';
              }
            }
          });

          // Hide main viewer images
          this.elements.viewer?.querySelectorAll('[data-media-id]').forEach((media) => {
            const img = media.querySelector('img');
            if (img) {
              const imgAlt = img.alt.toLowerCase();
              // New: check for _all matching
              let allMatch = false;
              if (imgAlt.endsWith('_all')) {
                const allPrefix = imgAlt.slice(0, -4); // remove '_all'
                allMatch = currentImgAlt.startsWith(allPrefix);
              }
              if (ignoreSharedImages) {
                // Hide only 'eam_all', but allow _all prefix matches
                media.style.display =
                  imgAlt === 'eam_all'
                    ? 'none'
                    : imgAlt === currentImgAlt || imgAlt === matchingAlt || allMatch
                    ? 'block'
                    : 'none';
              } else {
                media.style.display =
                  imgAlt === 'eam_all' || imgAlt === currentImgAlt || imgAlt === matchingAlt || allMatch
                    ? 'block'
                    : 'none';
              }
            }
          });
        } else {
          // Show all images if no featured media or alt text
          this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((thumbnail) => {
            thumbnail.style.display = 'block';
          });
          this.elements.viewer?.querySelectorAll('[data-media-id]').forEach((media) => {
            media.style.display = 'block';
          });
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
